import { css } from 'aphrodite';
import * as React from 'react';
import { Checkbox } from '../../../components-v2/Checkbox';
import { InputGroupAddon, InputGroupInput, InputGroupText, InputGroupV2 } from '../../../components-v2/InputGroup';
import { Pill } from '../../../components-v2/Pill/Pill';
import { conditionalOptionsSectionStyles as styles } from './styles/ConditionalOptionsSectionStyles';

interface ConditionalOptionsSectionProps {
	nodeId: string;
	canShowReplyOption: boolean;
	canShowClickOption: boolean;
	takeActionOnReply: boolean;
	takeActionOnClick: boolean;
	waitTimeoutDays: number;
	showTimeoutEditor: boolean;
	isDisabled: boolean;
	isUpdating: boolean;
	disabledReason: string | null;
	feedbackMessage: string | null;
	timeoutCaseLabel: string;
	onToggleReply: (checked: boolean) => void;
	onToggleClick: (checked: boolean) => void;
	onChangeTimeoutDays: (nextValue: number) => void;
}

export function ConditionalOptionsSection({
	nodeId,
	canShowReplyOption,
	canShowClickOption,
	takeActionOnReply,
	takeActionOnClick,
	waitTimeoutDays,
	showTimeoutEditor,
	isDisabled,
	isUpdating,
	disabledReason,
	feedbackMessage,
	timeoutCaseLabel,
	onToggleReply,
	onToggleClick,
	onChangeTimeoutDays,
}: ConditionalOptionsSectionProps) {
	const [timeoutDraftValue, setTimeoutDraftValue] = React.useState(() => String(waitTimeoutDays));

	React.useEffect(() => {
		setTimeoutDraftValue(String(waitTimeoutDays));
	}, [waitTimeoutDays]);

	if (!canShowReplyOption && !canShowClickOption) {
		return null;
	}

	const replyCheckboxId = `behavior-reply-${nodeId}`;
	const clickCheckboxId = `behavior-click-${nodeId}`;
	const timeoutInputId = `behavior-timeout-${nodeId}`;

	const commitTimeoutDraftValue = () => {
		const parsedValue = Number(timeoutDraftValue);
		const safeValue = Math.max(
			1,
			Math.min(365, Number.isFinite(parsedValue) ? Math.round(parsedValue) : waitTimeoutDays)
		);
		setTimeoutDraftValue(String(safeValue));
		if (safeValue !== waitTimeoutDays) {
			onChangeTimeoutDays(safeValue);
		}
	};

	const renderOptionCard = ({
		checkboxId,
		checked,
		title,
		description,
		onCheckedChange,
	}: {
		checkboxId: string;
		checked: boolean;
		title: string;
		description: string;
		onCheckedChange: (checked: boolean) => void;
	}) => (
		<div
			className={css(styles.optionCard, checked && styles.optionCardSelected, isDisabled && styles.optionCardDisabled)}
		>
			<div className={css(styles.optionCardHeader)}>
				<div className={css(styles.optionControlRow)}>
					<Checkbox
						id={checkboxId}
						size='lg'
						checked={checked}
						onCheckedChange={value => onCheckedChange(Boolean(value))}
						disabled={isDisabled}
					/>
					<label htmlFor={checkboxId} className={css(styles.optionContent)}>
						<span className={css(styles.optionTitle)}>{title}</span>
						<span className={css(styles.optionDescription)}>{description}</span>
					</label>
				</div>
				{checked && <Pill variant='label' color='blue' size='small' label='Active' />}
			</div>
		</div>
	);

	return (
		<section className={css(styles.container, isDisabled && styles.containerDisabled)} aria-label='Conditional actions'>
			<div className={css(styles.header)}>
				<div className={css(styles.headerText)}>
					<h4 className={css(styles.heading)}>Conditional actions</h4>
					<p className={css(styles.description)}>
						When enabled, contacts branch by engagement behavior after this step.
					</p>
				</div>
			</div>

			<div className={css(styles.optionsGrid)}>
				{canShowReplyOption &&
					renderOptionCard({
						checkboxId: replyCheckboxId,
						checked: takeActionOnReply,
						title: 'Take action if recipient replies',
						description: 'Create a reply branch and keep a timeout fallback for everyone else.',
						onCheckedChange: onToggleReply,
					})}

				{canShowClickOption &&
					renderOptionCard({
						checkboxId: clickCheckboxId,
						checked: takeActionOnClick,
						title: 'Take action if recipient clicks any links',
						description: 'Create a click branch while preserving the default path for non-clickers.',
						onCheckedChange: onToggleClick,
					})}
			</div>

			{showTimeoutEditor && (
				<div className={css(styles.timeoutEditor)}>
					<div className={css(styles.timeoutHeader)}>
						<div>
							<p className={css(styles.timeoutLabel)}>{timeoutCaseLabel}</p>
							<p className={css(styles.timeoutHint)}>
								Contacts who do not match selected engagement behaviors continue on the default pathway.
							</p>
						</div>
					</div>
					<div className={css(styles.timeoutControls)}>
						<label htmlFor={timeoutInputId} className={css(styles.timeoutInputLabel)}>
							Wait timeout
						</label>
						<InputGroupV2 styles={[styles.timeoutInputGroup]}>
							<InputGroupInput
								id={timeoutInputId}
								type='number'
								min={1}
								max={365}
								value={timeoutDraftValue}
								disabled={isDisabled}
								onChange={event => setTimeoutDraftValue(event.target.value)}
								onBlur={commitTimeoutDraftValue}
								onKeyDown={event => {
									if (event.key === 'Enter') {
										commitTimeoutDraftValue();
									}
								}}
							/>
							<InputGroupAddon align='inlineEnd'>
								<InputGroupText>days</InputGroupText>
							</InputGroupAddon>
						</InputGroupV2>
					</div>
				</div>
			)}

			{feedbackMessage && !isUpdating && (
				<p className={css(styles.feedbackMessage)} role='alert'>
					{feedbackMessage}
				</p>
			)}
			{isDisabled && disabledReason && !isUpdating && !feedbackMessage && (
				<p className={css(styles.disabledWarning)}>{disabledReason}</p>
			)}
		</section>
	);
}
